import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/card';
import { ArrowRight, UserPlus } from 'lucide-react';
import AbielAvatar from './AbielAvatar';
import { StandardDialogTemplate } from '@/components/shared/StandardDialogTemplate';
import GeneratedPrayerActionButtonsVertical from '@/pages/prayers/components/GeneratedPrayerActionButtonsVertical';
import PrayerChoiceController from '@/components/ai-prayer/choice/PrayerChoiceController';
import ShareDialog from '@/components/ShareDialog';
import { useShareDialog } from '@/hooks/useShareDialog';
import { usePrayerChoiceDialog } from '@/hooks/usePrayerChoiceDialog';
import { useToast } from '@/hooks/use-toast';
import { usePrayerStore } from '@/hooks/usePrayerStore';
import { usePrayerActions } from '@/hooks/usePrayerActions';
import { useEnhancedPrayerGeneration } from '@/hooks/useEnhancedPrayerGeneration';
import { PrayerInteractions, Prayer } from '@/types/prayer';
import { LAYERS } from '@/utils/layers';
import { criticalLogger } from '@/utils/logger';

import ClonedPrayersHero from '@/components/PrayerHeroSection';
import SearchFilterOverlay from '@/pages/prayers/components/SearchFilterOverlay';
import PrayerCardsGrid from '@/pages/prayers/components/PrayerCardsGrid';
import { usePrayersPage } from '@/pages/prayers/hooks/usePrayersPage';

import { useAbielAudioPlayer } from '@/hooks/useAbielAudioPlayer';
import { useAbielAudio } from '@/components/audio/AbielAudioContext';
import { DEFAULT_VOICE } from '@/constants/voiceConstants';

import AbielGuidanceDisplay from '../../companion/AbielGuidanceDisplay';

interface AbielGuidance {
  mode: 'guide';
  summary: string;
  reading_goals: string[];
  how_to_pray: {
    outline: {
      address: string;
      praise: string;
      confession?: string;
      request: string;
      submission: string;
      thanksgiving: string;
    };
    bullet_points: string[];
  };
  prompts: {
    en: string[];
    zh?: string[];
  };
  journaling_questions: string[];
  cross_references: string[];
  notes?: string;
}

interface GeneratedPrayerDisplayProps {
  prayer: string;
  onNewPrayer: () => void;
  onClose: () => void;
  onComment?: (prayer: any) => void;
}

// Type guard for AbielGuidance
function isAbielGuidance(obj: any): obj is AbielGuidance {
  return (
    obj &&
    typeof obj === 'object' &&
    obj.mode === 'guide' &&
    typeof obj.summary === 'string' &&
    Array.isArray(obj.reading_goals) &&
    obj.how_to_pray &&
    typeof obj.how_to_pray === 'object' &&
    obj.prompts &&
    typeof obj.prompts === 'object' &&
    Array.isArray(obj.journaling_questions) &&
    Array.isArray(obj.cross_references)
  );
}

export default function GeneratedPrayerDisplay({ 
  prayer, 
  onNewPrayer, 
  onClose,
  onComment
}: GeneratedPrayerDisplayProps) {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isAudioExpanded, setIsAudioExpanded] = useState(false);
  const [savedPrayerId, setSavedPrayerId] = useState<string | null>(null);

  const { 
    isAuthenticated, 
    recordChoiceAction 
  } = useEnhancedPrayerGeneration();

  const { addPrayer, userPrayers, communityPrayers } = usePrayerStore();
  const { toggleFavorite, toggleBookmark } = usePrayerActions();

  const { isOpen: isShareOpen, shareContent, openShareDialog, closeShareDialog } = useShareDialog();
  const {
    isOpen: isChoiceOpen,
    prayerContext,
    openChoiceDialog,
    closeChoiceDialog,
    handleINeedPrayer,
    handleIWantToPray,
    isAiAssistantOpen,
    closeAiAssistant
  } = usePrayerChoiceDialog();
  const { toast } = useToast();

  const prayersPageData = usePrayersPage();

  const [loading, setLoading] = useState<{ [key: string]: boolean }>({});
  const [error, setError] = useState<{ [key: string]: string | null }>({});

  // Memoize prayer object to avoid regenerating UUID on every render
  const prayerObject = useMemo(() => {
    return {
      id: crypto.randomUUID(),
      title: 'Abiel Prayer Guide',
      content: prayer,
      author: 'Abiel AI',
      date: new Date().toISOString(),
      category: 'Prayer Guide',
      status: 'published' as const,
      visibility: 'private' as const,
      group: null,
      isFavorite: false,
      isBookmarked: false,
      audioStoragePath: undefined,
      authorAvatarStoragePath: undefined
    };
  }, [prayer]);

  // Audio system integration
  const { togglePrayer, isPlayingPrayer, isLoading: audioLoading } = useAbielAudioPlayer();
  const { pause } = useAbielAudio();

  const existingPrayer = savedPrayerId 
    ? [...userPrayers, ...communityPrayers].find(p => p.id === savedPrayerId)
    : [...userPrayers, ...communityPrayers].find(p => p.content === prayer);

  const isFavorited = existingPrayer?.isFavorite || false;
  const isBookmarked = existingPrayer?.isBookmarked || false;

  const renderBackdropContent = () => (
    <div className="dialog-scaled">
      <div className="min-h-screen bg-golden-gradient dark:bg-dark-gradient">
        <ClonedPrayersHero />
        <div className="relative">
          <PrayerCardsGrid 
            prayers={prayersPageData.currentPrayers}
            onPrayerCardClick={(prayer: Prayer) => {}}
            onComment={(prayer: Prayer) => prayersPageData.handleComment(prayer.id)}
            onShare={(prayer: Prayer) => {}}
            onPrayerStatus={(id: string) => {}}
            onEdit={(id: string) => {}}
            onReport={(id: string) => {}}
            onDelete={(id: string) => {}}
            onPullToRefresh={prayersPageData.handleRefresh}
          />
          <SearchFilterOverlay
            searchTerm=""
            setSearchTerm={() => {}}
            selectedFilter={prayersPageData.activeTab}
            setSelectedFilter={() => {}}
            showFilters={false}
            setShowFilters={() => {}}
          />
        </div>
      </div>
    </div>
  );

  const ensurePrayerSaved = async () => {
    if (!isAuthenticated) {
      toast({
        title: "Sign up to save prayer guides",
        description: "Create an account to save your AI-generated prayer guides permanently.",
        action: (
          <button
            onClick={() => {
              window.location.href = '/auth';
            }}
            className="inline-flex items-center gap-1 text-sm font-medium text-primary hover:underline"
          >
            <UserPlus className="h-3 w-3" />
            Sign Up
          </button>
        ),
      });
      return null;
    }

    if (savedPrayerId || existingPrayer) {
      return existingPrayer;
    }

    const savedPrayer = await addPrayer({
      id: crypto.randomUUID(),
      date: new Date().toISOString(),
      title: 'Abiel Prayer Guide',
      content: prayer,
      author: 'Abiel AI',
      category: 'Prayer Guide',
      status: 'published',
      visibility: 'private',
      group: null,
      isFavorite: false,
      isBookmarked: false,
      audioStoragePath: undefined,
      authorAvatarStoragePath: undefined
    });

    if (savedPrayer) {
      setSavedPrayerId(savedPrayer.id);
      toast({
        title: "Prayer guide saved",
        description: "Your AI-generated prayer guide has been saved to your private collection.",
      });
      return savedPrayer;
    }
    return null;
  };

  const handleAction = async (name: string, action: () => void | Promise<void>) => {
    setLoading(prev => ({ ...prev, [name]: true }));
    setError(prev => ({ ...prev, [name]: null }));
    try {
      await Promise.resolve(action());
    } catch (err: any) {
      setError(prev => ({ ...prev, [name]: err.message ?? "An error occurred" }));
      toast({
        title: "Action failed",
        description: err.message || "Something went wrong.",
        variant: "destructive"
      });
    } finally {
      setLoading(prev => ({ ...prev, [name]: false }));
    }
  };

  const handleLocalShare = () => {
    const shareText = isAuthenticated 
      ? `Abiel Prayer Guide\n\n${prayer}\n\nShared from our prayer community`
      : `Abiel Prayer Guide\n\n${prayer}\n\nGenerated by Abiel - Sign up to save your guides!`;
    openShareDialog({
      title: 'Abiel Prayer Guide',
      content: prayer,
      shareText,
    });
  };

  const handleLocalBookmark = async () => {
    const prayerToBookmark = await ensurePrayerSaved();
    if (prayerToBookmark) {
      const success = await toggleBookmark(prayerToBookmark.id);
      if (!success) {
        criticalLogger.error('Failed to toggle bookmark');
      }
    }
  };

  const handleLocalFavorite = async () => {
    const prayerToFavorite = await ensurePrayerSaved();
    if (prayerToFavorite) {
      const success = await toggleFavorite(prayerToFavorite.id);
      if (!success) {
        criticalLogger.error('Failed to toggle favorite');
      }
    }
  };

  const handleLocalComment = () => {
    if (onComment) {
      onComment(prayerObject);
    } else {
      toast({
        title: "Comments not available",
        description: isAuthenticated 
          ? "Comments are not available for AI generated prayer guides" 
          : "Sign up to access comment features",
        variant: "destructive"
      });
    }
  };

  const handleLocalPray = async () => {
    try {
      await recordChoiceAction(prayerObject.id, 'i_need_prayer', 'ai_assistant');
    } catch (error) {
      criticalLogger.error('Failed to record choice action:', error);
    }
    onClose();
    openChoiceDialog({
      id: crypto.randomUUID(),
      title: 'Abiel Prayer Guide',
      content: prayer
    });
  };

  const interactions: PrayerInteractions = {
    isAnswered: false,
    isFavorite: isFavorited,
    hasComments: false,
    commentCount: 0,
    prayCount: 0,
  };

  // Real audio controller connected to the audio system
  const audioController = {
    isPlaying: isPlayingPrayer(prayerObject.id),
    playPrayerAudio: async (prayer: any) => {
      try {
        await togglePrayer(prayer, DEFAULT_VOICE);
      } catch (error) {
        console.error('Failed to play prayer audio:', error);
        toast({
          title: 'Audio playback failed',
          description: 'Unable to play audio. Please try again.',
          variant: 'destructive',
        });
      }
    },
    pausePrayerAudio: () => {
      pause?.();
    },
    shouldContinueOnNavigation: false,
    toggleBackgroundPlayback: () => {},
    handleNavigationAudio: (prayer: any) => {
      // Handle navigation audio if needed
    },
    cleanupAudioSystem: () => {},
  };

  const onPray = () => handleAction('pray', handleLocalPray);
  const onFavorite = () => handleAction('favorite', handleLocalFavorite);
  const onShare = () => handleAction('share', handleLocalShare);
  const onBookmark = () => handleAction('bookmark', handleLocalBookmark);
  const onCommentAction = () => handleAction('comment', handleLocalComment);
  const onMore = () => {
    toast({
      title: "More options",
      description: "Additional options for AI generated prayer guides",
    });
  };
  const onPrayerStatus = () => handleAction('status', () => {
    toast({
      title: "Prayer Guide Status",
      description: "Status options for AI generated prayer guides",
    });
  });
  const onEdit = () => handleAction('edit', () => {
    toast({
      title: "Edit Prayer Guide",
      description: "Editing not available for AI generated prayer guides",
      variant: "destructive"
    });
  });
  const onReport = () => handleAction('report', () => {
    toast({
      title: "Report Prayer Guide",
      description: "Report submitted for review",
    });
  });
  const onDelete = () => handleAction('delete', () => {
    toast({
      title: "Delete Prayer Guide",
      description: "AI generated prayer guide deleted",
      variant: "destructive"
    });
  });

  const handleToggleExpanded = () => {
    setIsExpanded(!isExpanded);
  };

  const handleAudioExpansionChange = useCallback((expanded: boolean) => {
    setIsAudioExpanded(expanded);
  }, []);

  const headerConfig = {
    variant: 'audio' as const,
    prayer: prayerObject,
    isExpanded,
    onToggle: handleToggleExpanded,
    onClose,
    showHints: true,
  };

  const audioConfig = {
    enabled: true,
    prayer: prayerObject,
    variant: 'golden' as const,
    collapsible: true,
    autoPlayWelcome: true,
    onExpansionChange: handleAudioExpansionChange,
  };

  const gestureCallbacks = {
    onExpand: () => setIsExpanded(true),
    onCollapse: () => setIsExpanded(false),
    onCollapseOrClose: onClose,
  };

  const backdropConfig = {
    enabled: true,
    onClick: onClose,
    showAsPreview: true,
    content: renderBackdropContent(),
    scaleTransform: 'scale(0.15)',
  };

  // Try to parse the prayer string as AbielGuidance format for display as a structured guide
  let abielGuidance: AbielGuidance | null = null;
  try {
    const parsed = JSON.parse(prayer);
    if (isAbielGuidance(parsed)) {
      abielGuidance = parsed;
    }
  } catch {}

  return (
    <>
      <StandardDialogTemplate
        isOpen={true}
        onClose={onClose}
        isExpanded={isExpanded}
        onToggleExpanded={handleToggleExpanded} 
        customZIndex={LAYERS.DIALOG_CONTENT}
        gesturePreset="expand-collapse"
        gestureCallbacks={gestureCallbacks}
        headerConfig={headerConfig}
        audioConfig={audioConfig}
        backdropConfig={backdropConfig}
        prayer={prayerObject}
        ariaLabel="Generated Prayer Guide Dialog"
      >
        <div className="flex-1 mx-4 space-y-4">
          {/* Welcome bubble */}
          <div className="flex justify-start">
            <div className="bg-muted text-foreground rounded-2xl rounded-bl-md px-4 py-3 max-w-[90%] mb-2 shadow">
              <div className="flex items-center gap-2 mb-1">
                <AbielAvatar size="sm" className="flex-shrink-0" />
                <span className="text-sm font-semibold">Abiel</span>
              </div>
              <div className="text-sm">
                Here’s your personalized prayer guide. Let’s pray with Scripture together.
              </div>
            </div>
          </div>
          {abielGuidance ? (
            <AbielGuidanceDisplay 
              guidance={abielGuidance} 
              esvReference={null}
              usedESVVerse={false}
            />
          ) : (
            <Card variant="glass-gold" className="w-full">
              <CardHeader>
                <CardTitle className="text-black dark:text-gold flex items-center gap-3">
                  <AbielAvatar size="sm" className="flex-shrink-0" />
                  <span className="flex-1">Abiel's Prayer Guide For You</span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className={`text-black dark:text-white whitespace-pre-wrap leading-relaxed ${!isExpanded ? 'line-clamp-5' : ''}`}>
                  {prayer}
                </div>
                {!isAuthenticated && (
                  <div className="mt-3 p-3 bg-blue-50/80 dark:bg-blue-900/30 rounded-lg border border-blue-200/60 dark:border-blue-800/60">
                    <div className="flex items-center gap-2 text-blue-800 dark:text-blue-200 text-sm">
                      <UserPlus className="h-3.5 w-3.5" />
                      <span className="font-medium">Save this prayer guide permanently</span>
                    </div>
                    <p className="text-blue-700 dark:text-blue-300 text-xs mt-1 mb-2">
                      Create a free account to save your AI-generated prayer guides and access additional features.
                    </p>
                    <div className="flex gap-2">
                      <button
                        onClick={() => window.location.href = '/auth'}
                        className="text-xs bg-blue-600 hover:bg-blue-700 text-white px-2.5 py-1 rounded-md transition-colors"
                      >
                        Sign Up Free
                      </button>
                      <button
                        onClick={() => window.location.href = '/auth'}
                        className="text-xs bg-transparent hover:bg-blue-600/10 text-blue-700 dark:text-blue-300 px-2.5 py-1 rounded-md border border-blue-300 dark:border-blue-700 transition-colors"
                      >
                        Sign In
                      </button>
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
          <div className="relative">
            <GeneratedPrayerActionButtonsVertical
              prayer={prayerObject}
              interactions={interactions}
              audioController={audioController}
              onPray={onPray}
              onFavorite={onFavorite}
              onShare={onShare}
              onBookmark={onBookmark}
              onComment={onCommentAction}
              onMore={onMore}
              onPrayerStatus={onPrayerStatus}
              onEdit={onEdit}
              onReport={onReport}
              onDelete={onDelete}
              loading={loading}
              error={error}
              positioning="absolute-above-audio"
            />
          </div>
          <div className="pt-4">
            <button
              onClick={() => {
                onNewPrayer();
              }}
              className="w-full bg-black hover:bg-black/90 text-white dark:bg-accent dark:hover:bg-accent/90 dark:text-accent-foreground font-medium py-3 rounded-lg transition-colors flex items-center justify-center gap-2"
            >
              <ArrowRight className="w-4 h-4" />
              Regenerate Prayer Guide
            </button>
          </div>
        </div>
      </StandardDialogTemplate>
      <PrayerChoiceController
        isOpen={isChoiceOpen}
        onClose={closeChoiceDialog}
        prayerContext={prayerContext}
        onINeedPrayer={handleINeedPrayer}
        onIWantToPray={handleIWantToPray}
        isAiAssistantOpen={isAiAssistantOpen}
        onCloseAiAssistant={closeAiAssistant}
      />
      <ShareDialog
        isOpen={isShareOpen}
        onClose={closeShareDialog}
        content={shareContent}
      />
    </>
  );
}
